export class ApiError extends Error {
  constructor(public status: number, public body: unknown, message: string) {
    super(message);
    this.name = "ApiError";
  }
}

function getApiKey(): string {
  return localStorage.getItem("apiKey") ?? "";
}

export const AUTH_REDIRECT_EVENT = "auth:redirect";

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const r = await fetch(path, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      "X-API-Key": getApiKey(),
      ...(init.headers ?? {}),
    },
  });
  if (!r.ok) {
    const body = await r.json().catch(() => null) as { error?: string } | null;
    if (r.status === 401) {
      window.dispatchEvent(new CustomEvent(AUTH_REDIRECT_EVENT));
    }
    throw new ApiError(r.status, body, body?.error ?? r.statusText);
  }
  return r.json() as Promise<T>;
}

import { useEffect, useReducer, useRef, useState, useCallback } from "react";

type ApiState<T> = { data?: T; error?: Error; loading: boolean };

export function useApi<T>(
  path: string | null,
  deps: unknown[] = []
): ApiState<T> & { refetch: () => void } {
  const [state, setState] = useState<ApiState<T>>({ loading: path !== null });
  const [tick, refetch] = useReducer((n: number) => n + 1, 0);
  const aborterRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (path === null) {
      setState({ loading: false });
      return;
    }
    aborterRef.current?.abort();
    const ac = new AbortController();
    aborterRef.current = ac;
    setState({ loading: true });
    apiFetch<T>(path, { signal: ac.signal })
      .then((data) => { if (!ac.signal.aborted) setState({ loading: false, data }); })
      .catch((error: unknown) => {
        if (ac.signal.aborted) return;
        if (error instanceof Error && error.name === "AbortError") return;
        setState({ loading: false, error: error as Error });
      });
    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, tick, ...deps]);

  return { ...state, refetch: () => refetch() };
}

type Method = "POST" | "PUT" | "DELETE" | "PATCH";

export function useApiMutation<TResp = unknown, TBody = unknown>(): {
  mutate: (path: string, method: Method, body?: TBody) => Promise<TResp>;
  loading: boolean;
  error?: Error;
} {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>();
  const mutate = useCallback(async (path: string, method: Method, body?: TBody): Promise<TResp> => {
    setLoading(true);
    setError(undefined);
    try {
      const result = await apiFetch<TResp>(path, {
        method,
        body: body !== undefined ? JSON.stringify(body) : undefined,
      });
      setLoading(false);
      return result;
    } catch (e) {
      setLoading(false);
      setError(e as Error);
      throw e;
    }
  }, []);
  return { mutate, loading, error };
}
